import * as React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { enqueueSnackbar, type VariantType } from 'notistack';

export type ConfirmOptions = {
    title?: string;
    message: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    severity?: 'info' | 'warning' | 'error';
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let setPending: ((p: Pending | null) => void) | null = null;

/**
 * Imperative replacement for `window.confirm`. Returns a Promise that
 * resolves to true on confirm, false on cancel. Falls back to the native
 * dialog if the host hasn't mounted yet.
 */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
    if (!setPending) {
        const fallback = typeof opts.message === 'string'
            ? opts.message
            : (opts.title ?? 'Are you sure?');
        return Promise.resolve(window.confirm(fallback));
    }
    return new Promise<boolean>(resolve => {
        setPending!({ ...opts, resolve });
    });
}

/**
 * Imperative replacement for `window.alert`, routed to notistack.
 *
 * Errors are sticky (`autoHideDuration: null`) — they often carry long stack
 * traces or remediation steps the user needs time to read or copy. Other
 * variants auto-hide after a few seconds. Every snackbar has a close button
 * configured globally on the SnackbarProvider in src/index.tsx.
 */
export function notify(opts: { message: React.ReactNode; severity?: VariantType }) {
    const isError = opts.severity === 'error';
    enqueueSnackbar(opts.message, {
        variant: opts.severity ?? 'default',
        autoHideDuration: isError ? null : 4000,
        persist: isError,
    });
}

export const DialogHost: React.FC = () => {
    const [pending, _setPending] = React.useState<Pending | null>(null);

    React.useEffect(() => {
        setPending = _setPending;
        return () => {
            setPending = null;
        };
    }, []);

    const handleClose = (ok: boolean) => {
        if (pending) {
            pending.resolve(ok);
            _setPending(null);
        }
    };

    if (!pending) return null;

    const isDestructive = pending.severity === 'error';
    const isWarning = pending.severity === 'warning';

    return (
        <Dialog
            open={true}
            onClose={() => handleClose(false)}
            slotProps={{ paper: { sx: { minWidth: 360, borderRadius: 2 } } }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                {pending.title ?? (isDestructive ? 'Confirm action' : 'Are you sure?')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                    {pending.message}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => handleClose(false)} color="inherit">
                    {pending.cancelLabel ?? 'Cancel'}
                </Button>
                <Button
                    onClick={() => handleClose(true)}
                    variant="contained"
                    color={isDestructive ? 'error' : isWarning ? 'warning' : 'primary'}
                    autoFocus
                >
                    {pending.confirmLabel ?? (isDestructive ? 'Delete' : 'Confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
